import React, { useState } from 'react';
import InfoDialog from '../common/InfoDialog';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import { 
  Avatar, 
  Box, 
  Typography, 
  TextField, 
  IconButton
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';

export const Comment = ({ comment, onDelete }) => {
  const { user } = useAuth();
  const isOwner = user?.id === comment.user_id;

  return (
    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
      <Avatar src={comment.profiles?.avatar_url} alt={comment.profiles?.full_name} />
      <Box sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography variant="subtitle2" fontWeight="bold">
            {comment.profiles?.full_name || 'Anonymous'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
          </Typography>
          {isOwner && (
            <IconButton size="small" onClick={() => onDelete(comment.id)}>
              <Typography variant="caption" color="error">
                Delete
              </Typography>
            </IconButton>
          )}
        </Box>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{comment.content}</Typography>
      </Box>
    </Box>
  );
};

export const CommentInput = ({ postId, onCommentAdded }) => {
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [infoDialog, setInfoDialog] = useState({ open: false, title: '', message: '', severity: 'info' });
  const { user } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const response = await api.post('/comments', {
        content: content.trim(),
        post_id: postId,
      });
      const { data, error } = { data: response.data.comment, error: null }; // Adapt to new response shape

      if (error) throw error;
      
      setContent('');
      if (onCommentAdded) onCommentAdded(data);
    } catch (error) {
      console.error('Error adding comment:', error);
      setInfoDialog({
        open: true,
        title: 'Comment Failed',
        message: `Failed to add comment: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2, display: 'flex', gap: 1 }}>
      <Avatar src={user?.user_metadata?.avatar_url} sx={{ width: 40, height: 40 }} />
      <TextField
        fullWidth
        size="small"
        variant="outlined"
        placeholder="Write a comment..."
        value={content}
        onChange={(e) => setContent(e.target.value)}
        InputProps={{
          endAdornment: (
            <IconButton 
              type="submit" 
              color="primary" 
              disabled={!content.trim() || isSubmitting}
            >
              <SendIcon />
            </IconButton>
          ),
        }}
      />
    </Box>

    <InfoDialog
      open={infoDialog.open}
      onClose={() => setInfoDialog({ open: false, title: '', message: '', severity: 'info' })}
      title={infoDialog.title}
      message={infoDialog.message}
      severity={infoDialog.severity}
    />
    </>
  );
};
